import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { UsagePatterns } from '@/api/billing/billing.types';
import { Clock, Sun } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HourlyUsageChartProps {
  patterns: UsagePatterns | null;
  isLoading: boolean;
  onError: (error: string) => void;
  className?: string;
}

export const HourlyUsageChart = ({ patterns, isLoading, onError, className }: HourlyUsageChartProps) => {
  if (isLoading) {
    return (
      <Card className={cn("h-full shadow-sm", className)}>
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-lg" />
              <Skeleton className="h-5 w-40" />
            </div>
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="h-[280px] relative">
            <Skeleton className="h-full w-full rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!patterns?.byHour || patterns.byHour.length === 0) {
    return (
      <Card className={cn("h-full shadow-sm", className)}>
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Clock className="h-4 w-4 text-primary" />
            </div>
            <div className="space-y-0.5">
              <CardTitle className="text-base leading-none">Hourly Usage</CardTitle>
              <CardDescription className="text-xs text-muted-foreground/70 leading-none">
                Call distribution by hour of day
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[calc(100%-73px)]">
          <div className="text-center space-y-3">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">No Hourly Data</p>
              <p className="text-xs text-muted-foreground">Hourly usage patterns will appear here after calls are made</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const peakHours = patterns.peakHours || [];
  const peakHour = peakHours.length > 0 ? peakHours[0].toString().padStart(2, '0') : null;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const hour = parseInt(label);
      const timeFormatted = `${hour.toString().padStart(2, '0')}:00`;
      
      return (
        <div className="bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg p-3">
          <p className="font-medium mb-1">{timeFormatted}</p>
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: payload[0].color }}
            />
            <span className="text-sm">{value.toLocaleString()} calls</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut", delay: 0.2 }}
      className={className}
    >
      <Card className="h-full shadow-sm">
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <Clock className="h-4 w-4 text-primary" />
              </div>
              <div className="space-y-0.5">
                <CardTitle className="text-base leading-none">Hourly Usage</CardTitle>
                <CardDescription className="text-xs text-muted-foreground/70 leading-none">
                  Call distribution by hour of day
                </CardDescription>
              </div>
            </div>
            {peakHour && (
              <Badge variant="outline" className="flex items-center gap-1 text-xs">
                <Sun className="h-3 w-3" />
                Peak: {peakHour}:00
              </Badge>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="pt-6">
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={patterns.byHour} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <defs>
                  <linearGradient id="hourlyGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                
                <XAxis 
                  dataKey="hour"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(value) => `${value.toString().padStart(2, '0')}:00`}
                  interval={2} // Show every 2 hours to avoid crowding
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  width={40}
                />
                
                <Tooltip content={<CustomTooltip />} />
                
                <Area 
                  type="monotone" 
                  dataKey="calls" 
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  fill="url(#hourlyGradient)"
                  dot={false}
                  activeDot={{ 
                    r: 4, 
                    fill: 'hsl(var(--primary))',
                    stroke: 'hsl(var(--background))',
                    strokeWidth: 2
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
